import { useEffect, useState } from 'react';
import { api, ErreurApi } from './api.js';
import { tokens } from './tokens.js';
import { LoginDolibarr } from './components/LoginDolibarr.jsx';
import { Dashboard } from './components/Dashboard.jsx';
import { CreerTicket } from './components/CreerTicket.jsx';
import { TicketDetail } from './components/TicketDetail.jsx';
import { AdminDashboard } from './components/AdminDashboard.jsx';
import { AdminServices } from './components/AdminServices.jsx';
import { AdminUtilisateurs } from './components/AdminUtilisateurs.jsx';
import { AdminApplications } from './components/AdminApplications.jsx';
import { AdminGabaritsMail } from './components/AdminGabaritsMail.jsx';
import { AdminSentry } from './components/AdminSentry.jsx';
import { AdminTableauBord } from './components/AdminTableauBord.jsx';
import { AdminParametres } from './components/AdminParametres.jsx';
import { NotificationsBadge } from './components/NotificationsBadge.jsx';
import { LogOut, LifeBuoy, Inbox, Plus, Settings, Users, Layers, Boxes, Mail, Activity, BarChart3, Sliders } from 'lucide-react';

// Navigation state-based. Vues possibles :
//   dashboard | creer | detail | admin-tickets | admin-services | admin-users | admin-apps
export function App() {
  const [chargement, setChargement] = useState(true);
  const [utilisateur, setUtilisateur] = useState(null);
  const [vue, setVue] = useState('dashboard');
  const [tokenSelectionne, setTokenSelectionne] = useState(null);
  const [menuAdminOuvert, setMenuAdminOuvert] = useState(false);
  // Origine du detail : memorise depuis quelle vue on a ouvert un ticket,
  // pour que le bouton Retour y renvoie.
  const [origineDetail, setOrigineDetail] = useState('dashboard');

  useEffect(() => {
    api.get('/auth/me')
      .then(d => setUtilisateur(d.utilisateur))
      .catch(e => {
        if (!(e instanceof ErreurApi) || e.statut !== 401) console.error('/me :', e);
      })
      .finally(() => setChargement(false));
  }, []);

  async function deconnecter() {
    try { await api.post('/auth/logout'); } catch {}
    setUtilisateur(null);
    setVue('dashboard');
    setTokenSelectionne(null);
  }

  function naviguer(nouvelleVue, token = null) {
    // Memorise la vue d'origine quand on ouvre un detail (sauf si on saute
    // d'un detail a un autre via NotificationsBadge — on garde l'origine).
    if (nouvelleVue === 'detail' && vue !== 'detail') {
      setOrigineDetail(vue);
    }
    setVue(nouvelleVue);
    setTokenSelectionne(token);
    setMenuAdminOuvert(false);
  }

  if (chargement) {
    return (
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'center',
        height:'100vh', color: tokens.inkSoft, fontSize: 14,
      }}>Chargement…</div>
    );
  }

  if (!utilisateur) {
    return <LoginDolibarr onLogin={setUtilisateur} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: tokens.bg }}>
      <header style={{
        background: tokens.surface,
        borderBottom: `1px solid ${tokens.border}`,
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <div
          onClick={() => naviguer('dashboard')}
          style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}
        >
          <LifeBuoy size={22} color={tokens.accent} />
          <span style={{ fontSize: 16, fontWeight: 600, color: tokens.ink }}>
            Tickets AMC
          </span>
        </div>

        <nav style={{ display:'flex', alignItems:'center', gap: 4, flex: 1, marginLeft: 32 }}>
          <BoutonNav active={vue === 'dashboard'} onClick={() => naviguer('dashboard')}>
            <Inbox size={14} /> Mes tickets
          </BoutonNav>
          <BoutonNav active={vue === 'creer'} onClick={() => naviguer('creer')}>
            <Plus size={14} /> Nouveau
          </BoutonNav>

          {utilisateur.est_admin && (
            <div
              style={{ position:'relative' }}
              onMouseEnter={() => setMenuAdminOuvert(true)}
              onMouseLeave={() => setMenuAdminOuvert(false)}
            >
              <BoutonNav
                active={vue.startsWith('admin-')}
                onClick={() => naviguer('admin-bord')}
              >
                <Settings size={14} /> Administration ▾
              </BoutonNav>
              {menuAdminOuvert && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0,
                  marginTop: 0,
                  paddingTop: 4,
                  background: tokens.surface,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: 8,
                  padding: 6,
                  minWidth: 220,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                }}>
                  <BoutonMenu onClick={() => naviguer('admin-bord')} icon={BarChart3}>
                    Tableau de bord
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-tickets')} icon={Inbox}>
                    Tous les tickets
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-services')} icon={Layers}>
                    Services / sous-services
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-users')} icon={Users}>
                    Utilisateurs
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-apps')} icon={Boxes}>
                    Applications
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-mail')} icon={Mail}>
                    Modèles de mail
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-sentry')} icon={Activity}>
                    Intégration Sentry
                  </BoutonMenu>
                  <BoutonMenu onClick={() => naviguer('admin-parametres')} icon={Sliders}>
                    Paramètres
                  </BoutonMenu>
                </div>
              )}
            </div>
          )}
        </nav>

        <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
          <NotificationsBadge onOuvrirTicket={(token) => naviguer('detail', token)} />
          <div style={{ fontSize: 13, color: tokens.inkSoft }}>
            {utilisateur.nom_complet}
            {utilisateur.est_admin && (
              <span style={{
                marginLeft: 6, padding: '1px 6px', borderRadius: 4,
                background: tokens.accentBg, color: tokens.accent,
                fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
              }}>admin</span>
            )}
          </div>
          <button
            onClick={deconnecter}
            style={{
              background:'transparent',
              border:`1px solid ${tokens.border}`,
              color: tokens.inkSoft,
              padding: '6px 10px', borderRadius: 6, fontSize: 12,
              display:'flex', alignItems:'center', gap: 4, cursor:'pointer',
            }}
          >
            <LogOut size={12} /> Déconnexion
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px' }}>
        {vue === 'dashboard' && (
          <Dashboard
            utilisateur={utilisateur}
            onNouveau={() => naviguer('creer')}
            onOuvrirTicket={(token) => naviguer('detail', token)}
          />
        )}
        {vue === 'creer' && (
          <CreerTicket
            onRetour={() => naviguer('dashboard')}
            onCree={(token) => naviguer('detail', token)}
          />
        )}
        {vue === 'detail' && tokenSelectionne && (
          <TicketDetail
            token={tokenSelectionne}
            utilisateur={utilisateur}
            onRetour={() => naviguer(origineDetail || 'dashboard')}
          />
        )}
        {vue === 'admin-tickets' && utilisateur.est_admin && (
          <AdminDashboard onOuvrirTicket={(token) => naviguer('detail', token)} />
        )}
        {vue === 'admin-services' && utilisateur.est_admin && (
          <AdminServices />
        )}
        {vue === 'admin-users' && utilisateur.est_admin && (
          <AdminUtilisateurs utilisateurActuel={utilisateur} />
        )}
        {vue === 'admin-apps' && utilisateur.est_admin && (
          <AdminApplications />
        )}
        {vue === 'admin-mail' && utilisateur.est_admin && (
          <AdminGabaritsMail utilisateurActuel={utilisateur} />
        )}
        {vue === 'admin-sentry' && utilisateur.est_admin && (
          <AdminSentry />
        )}
        {vue === 'admin-bord' && utilisateur.est_admin && (
          <AdminTableauBord onOuvrirTicket={(token) => naviguer('detail', token)} />
        )}
        {vue === 'admin-parametres' && utilisateur.est_admin && (
          <AdminParametres />
        )}
      </main>
    </div>
  );
}

function BoutonNav({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        display:'inline-flex', alignItems:'center', gap: 6,
        background: active ? tokens.accentBg : 'transparent',
        color: active ? tokens.accent : tokens.inkSoft,
        border: 'none',
        padding: '7px 12px', borderRadius: 6,
        fontSize: 13, fontWeight: 500, cursor:'pointer',
      }}
    >{children}</button>
  );
}

function BoutonMenu({ onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        display:'flex', alignItems:'center', gap: 10,
        width: '100%',
        background: 'transparent', border: 'none',
        padding: '8px 10px', borderRadius: 6,
        fontSize: 13, color: tokens.ink, textAlign:'left', cursor:'pointer',
      }}
      onMouseEnter={(e) => e.currentTarget.style.background = tokens.surfaceAlt}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
    >
      <Icon size={14} color={tokens.inkSoft} />
      {children}
    </button>
  );
}
